import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { DeleteEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/deleteEmailTemplateRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useDeleteEmailTemplateRPCClientImpl } from "../rpcs/RPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { RootStore } from "../../root/store/RootStore";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";

const getInitialState = (): SnapshotIn<typeof DeleteEmailTemplateStore> => ({
  templateId: EMPTY_CHARACTER,
  emailTemplateName: createBoundedNameModel({
    name: EMPTY_CHARACTER,
  }),
  deleteEmailTemplateRPCError: null,
});

export const DeleteEmailTemplateStore = types
  .model("DeleteEmailTemplateStore", {
    templateId: types.string,
    emailTemplateName: BoundedNameModel,
    deleteEmailTemplateRPCError: types.maybeNull(
      types.enumeration(
        "DeleteEmailTemplateRPCError",
        Object.values(DeleteEmailTemplateRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    setTemplateId: (templateId: string): void => {
      store.templateId = templateId;
    },
    deleteEmailTemplate: flow(function* () {
      store.deleteEmailTemplateRPCError = null;

      const apiClient = getAPIClient(store);
      const request = new DeleteEmailTemplateRPC.Request(
        new LeoUUID(store.templateId),
      );

      const result: LeoRPCResult<
        DeleteEmailTemplateRPC.Response,
        DeleteEmailTemplateRPC.Errors.Errors
      > = yield useDeleteEmailTemplateRPCClientImpl(apiClient).execute(request);
      if ("response" in result) {
        const emailTemplatesStore =
          getRoot<typeof RootStore>(store).emailTemplatesStore;
        emailTemplatesStore.getEmailTemplates();
      } else {
        switch (result.error.code) {
          case DeleteEmailTemplateRPC.RPCError.InvalidTemplateId:
            store.deleteEmailTemplateRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createDeleteEmailTemplateStore = (): Instance<
  typeof DeleteEmailTemplateStore
> => DeleteEmailTemplateStore.create(getInitialState());
